import HomeIcon from '@mui/icons-material/Home';
import {
  ListItem,
  Chip,
  Stack,
  Divider,
  styled,
  emphasize,
  Breadcrumbs,
} from '@mui/material';
import EmptyMessage from 'core/components/EmptyMessage';
import { findGroupById } from 'core/utils/productGroupUtils';
import { isNil, isEmpty } from 'lodash';
import { useId, useMemo } from 'react';
import type { ProductGroup } from 'models/product/productGroup';
import type { FC } from 'react';

interface TileProductGroupSelectProps {
  groups?: ProductGroup[];
  selectedItems?: string | null;
  onNodeSelect?: (id: number) => void;
}

const BreadcrumbChip = styled(Chip)(({ theme }) => {
  const backgroundColor =
    theme.palette.mode === 'light' ? theme.palette.grey[100] : theme.palette.grey[800];
  return {
    backgroundColor,
    height: theme.spacing(3),
    color: theme.palette.text.primary,
    fontWeight: theme.typography.fontWeightRegular,
    '&:hover, &:focus': {
      backgroundColor: emphasize(backgroundColor, 0.06),
    },
    '&:active': {
      boxShadow: theme.shadows[1],
      backgroundColor: emphasize(backgroundColor, 0.12),
    },
  };
}) as typeof Chip;

const TileProductGroupSelect: FC<TileProductGroupSelectProps> = (props) => {
  const { groups = [], onNodeSelect, selectedItems } = props;

  const componentId = useId();

  const current = useMemo(
    () =>
      isNil(selectedItems) || +selectedItems < 0
        ? { group: { groups, id: -10, name: '' }, path: [] }
        : findGroupById(groups ?? [], +(selectedItems ?? 0)),
    [groups, selectedItems]
  );

  if (isEmpty(current?.path) && isEmpty(groups)) {
    return <EmptyMessage />;
  }

  return (
    <>
      <Breadcrumbs aria-label='breadcrumb'>
        {!isNil(current) && current?.group.id > 0 && (
          <BreadcrumbChip
            sx={{
              '& .MuiChip-label': { display: 'none' },
              '& .MuiChip-icon': { mr: '5px' },
            }}
            icon={<HomeIcon fontSize='small' />}
            onClick={() => {
              !isNil(onNodeSelect) && onNodeSelect(-1);
            }}
          />
        )}

        {!isNil(current) &&
          current.group.id > 0 &&
          current.path.map((g, i) => (
            <BreadcrumbChip
              key={`${componentId}-group-path-${i}`}
              label={g.name}
              onClick={() => {
                !isNil(onNodeSelect) && onNodeSelect(g.id);
              }}
            />
          ))}
      </Breadcrumbs>

      {!isNil(current) &&
        current.group.id > 0 &&
        !isNil(groups) &&
        !isEmpty(groups) &&
        !isEmpty(current.group.groups) && <Divider />}

      {!isNil(groups) && !isEmpty(groups) && !isEmpty(current?.group.groups) && (
        <Stack
          component={'ul'}
          direction={'row'}
          flexWrap={'wrap'}
          margin={0}
          padding={0}
          gap={1}>
          {current?.group.groups.map((g) => (
            <ListItem
              key={`${componentId}-${g.id}`}
              disablePadding
              disableGutters
              onClick={() => {
                !isNil(onNodeSelect) && onNodeSelect(g.id);
              }}
              sx={{ width: 'auto' }}>
              <Chip
                color={
                  selectedItems === `${g.id}` || (isNil(selectedItems) && g.id <= 0)
                    ? 'primary'
                    : 'default'
                }
                label={g.name}
              />
            </ListItem>
          ))}
        </Stack>
      )}
    </>
  );
};

export default TileProductGroupSelect;
